// One Daily call for one meeting session. It lives in <CallSession/>, which never remounts while the session lasts,
// so switching between full screen, the corner tile and the pop-out window never touches the connection.
// Lifecycle: lobby (camera preview, devices) → joining → joined; an unplanned drop → 'left' with Rejoin.
import { useCallback, useEffect, useRef, useState } from 'react';
import DailyIframe from '@daily-co/daily-js';
import { toast } from 'sonner';
import { loadBackground } from './backgrounds';

const PREFS = 'strata.meeting.prefs';
const readPrefs = () => {
  try { return { cam: true, mic: true, ...JSON.parse(localStorage.getItem(PREFS) || '{}') }; } catch { return { cam: true, mic: true }; }
};
const savePrefs = (p) => { try { localStorage.setItem(PREFS, JSON.stringify({ ...readPrefs(), ...p })); } catch { /* private mode */ } };

const devicesOf = (list, kind) => (list || []).filter((d) => d.kind === kind && d.deviceId);
// Daily's fatal errors that mean the meeting is over (not a network drop): no Rejoin.
const ENDED = new Set(['ejected', 'exp-room', 'no-room', 'end-of-life']);
const NONE = { type: 'none' };

async function processorFor(eff) {
  if (eff.type === 'blur') return { type: 'background-blur', config: { strength: 0.6 } };
  if (eff.type === 'image') return { type: 'background-image', config: { source: await loadBackground(eff) } };
  return { type: 'none' };
}

async function freshCallObject() {
  try { const ex = DailyIframe.getCallInstance?.(); if (ex) await ex.destroy(); } catch { /* noop */ }
  const make = () => DailyIframe.createCallObject({ subscribeToTracksAutomatically: true });
  try { return make(); } catch {
    try { const ex = DailyIframe.getCallInstance?.(); if (ex) await ex.destroy(); } catch { /* noop */ }
    return make();
  }
}

export function useDailyCall({ roomUrl, token, onLeave, hostId: initialHost }) {
  const callRef = useRef(null);
  const onLeaveRef = useRef(onLeave);
  const leaving = useRef(false);
  const ended = useRef(false);
  const handlers = useRef(new Set());
  const [hostId, setHostId] = useState(initialHost ? String(initialHost) : null);
  const hostRef = useRef(hostId);
  hostRef.current = hostId;
  const [phase, setPhase] = useState('lobby');
  const [participants, setParticipants] = useState({});
  const [error, setError] = useState('');
  const [mediaError, setMediaError] = useState('');
  const [devices, setDevices] = useState({ cameras: [], mics: [], speakers: [], camId: '', micId: '', speakerId: readPrefs().speakerId || '' });
  const [network, setNetwork] = useState({ interrupted: false, quality: 'good' });
  const [micLevel, setMicLevel] = useState(0);
  const [activeSpeaker, setActiveSpeaker] = useState(null);
  const [joinedAt, setJoinedAt] = useState(null);
  const [effect, setEffectState] = useState(NONE);
  const [effectBusy, setEffectBusy] = useState(null);
  const [localBg, setLocalBg] = useState(null);
  const blurSupported = !!DailyIframe.supportedBrowser?.()?.supportsVideoProcessing;

  useEffect(() => { onLeaveRef.current = onLeave; }, [onLeave]);

  const refresh = useCallback(() => {
    const c = callRef.current;
    if (c) setParticipants({ ...c.participants() });
  }, []);

  const loadDevices = useCallback(async () => {
    const c = callRef.current;
    if (!c) return;
    try {
      const [{ devices: list }, cur] = await Promise.all([c.enumerateDevices(), c.getInputDevices()]);
      const speakers = devicesOf(list, 'audiooutput');
      setDevices((d) => ({ cameras: devicesOf(list, 'videoinput'), mics: devicesOf(list, 'audioinput'), speakers,
        camId: cur?.camera?.deviceId || '', micId: cur?.mic?.deviceId || '',
        speakerId: speakers.some((s) => s.deviceId === d.speakerId) ? d.speakerId : (cur?.speaker?.deviceId || speakers[0]?.deviceId || '') }));
    } catch { /* devices unavailable */ }
  }, []);

  // Background effect: none, blur or a picture ({ type: 'image', id, bytes? }).
  const applyEffect = useCallback(async (eff) => {
    const c = callRef.current;
    if (!c) return false;
    try {
      await c.updateInputSettings({ video: { processor: await processorFor(eff) } });
      setEffectState(eff);
      return true;
    } catch { return false; }
  }, []);

  useEffect(() => {
    if (!roomUrl) return undefined;
    let cancelled = false;
    let call;
    (async () => {
      call = await freshCallObject();
      if (cancelled) { call.destroy().catch(() => {}); return; }
      callRef.current = call;
      ['participant-joined', 'participant-updated', 'participant-left', 'track-started', 'track-stopped']
        .forEach((ev) => call.on(ev, refresh));
      call.on('joined-meeting', () => {
        if (cancelled) return;
        setPhase('joined');
        setError('');
        setJoinedAt((t) => t || Date.now());
        refresh();
      });
      call.on('active-speaker-change', (e) => { if (!cancelled) setActiveSpeaker(e?.activeSpeaker?.peerId || null); });
      call.on('left-meeting', () => {
        if (cancelled) return;
        if (ended.current) setPhase('ended');
        else if (leaving.current) onLeaveRef.current?.();
        else setPhase('left');
      });
      call.on('error', (e) => {
        if (cancelled) return;
        if (ENDED.has(e?.error?.type)) { ended.current = true; setPhase('ended'); return; }
        setError(e?.errorMsg || 'The video connection failed.');
        setPhase('left');
      });
      call.on('camera-error', (e) => { if (!cancelled) setMediaError(e?.errorMsg?.errorMsg || e?.error?.msg || 'Camera or microphone unavailable.'); });
      call.on('nonfatal-error', (e) => { if (!cancelled && e?.type === 'video-processor-error') setEffectState(NONE); });
      call.on('network-connection', (e) => {
        if (cancelled) return;
        if (e?.event === 'interrupted') setNetwork((n) => ({ ...n, interrupted: true }));
        if (e?.event === 'connected') setNetwork((n) => ({ ...n, interrupted: false }));
      });
      call.on('network-quality-change', (e) => { if (!cancelled && e?.threshold) setNetwork((n) => ({ ...n, quality: e.threshold })); });
      call.on('available-devices-updated', loadDevices);
      call.on('local-audio-level', (e) => { if (!cancelled) setMicLevel(e?.audioLevel || 0); });
      call.on('app-message', (ev) => {
        if (ev?.data?.mm === 'ended') {  // the host ended it for everyone
          ended.current = true;
          setPhase('ended');
          call.leave().catch(() => {});
          return;
        }
        if (ev?.data?.mm === 'lower-hand') {  // only the host (or a meeting owner) may lower someone's hand
          const all = call.participants();
          const from = all[ev.fromId];
          const allowed = from?.owner || (hostRef.current && from?.user_id === hostRef.current);
          if (allowed && all.local?.userData?.hand) call.setUserData({ ...all.local.userData, hand: null }).catch(() => {});
          return;
        }
        if (ev?.data?.mm === 'host' && ev.fromId === 'API' && ev.data.uid) setHostId(String(ev.data.uid));  // server-sent only
        handlers.current.forEach((h) => h(ev));
      });
      const p = readPrefs();
      try {
        await call.startCamera({ url: roomUrl, token, startVideoOff: !p.cam, startAudioOff: !p.mic,
          ...(p.camId ? { videoSource: p.camId } : {}), ...(p.micId ? { audioSource: p.micId } : {}) });
      } catch (e) {
        if (!cancelled) setMediaError(String(e?.message || e || 'Camera or microphone unavailable.'));
      }
      if (cancelled) return;
      refresh();
      loadDevices();
      const eff = p.effect || (p.blur ? { type: 'blur' } : null);
      if (eff && eff.type !== 'none' && blurSupported) applyEffect(eff).then((ok) => { if (!ok) savePrefs({ effect: NONE, blur: undefined }); });
      call.startLocalAudioLevelObserver?.(120).catch(() => {});
    })();
    return () => {
      cancelled = true;
      const c = callRef.current;
      callRef.current = null;
      if (c) c.leave().catch(() => {}).finally(() => c.destroy().catch(() => {}));
    };
  }, [roomUrl, token, refresh, loadDevices, applyEffect, blurSupported]);

  const call = () => callRef.current;
  const local = participants.local;

  const join = useCallback(async () => {
    const c = callRef.current;
    if (!c) return;
    leaving.current = false;
    setPhase('joining');
    setError('');
    try { c.stopLocalAudioLevelObserver?.(); } catch { /* noop */ }
    const me = c.participants()?.local;
    savePrefs({ cam: !!me?.video, mic: !!me?.audio });
    try {
      await c.join({ url: roomUrl, token });
    } catch (e) {
      setError(String(e?.message || e || "Couldn't join the meeting."));
      setPhase('left');
    }
  }, [roomUrl, token]);

  const leave = useCallback(() => {
    leaving.current = true;
    const c = callRef.current;
    if (c && (phase === 'joined' || phase === 'joining')) c.leave().catch(() => onLeaveRef.current?.());
    else onLeaveRef.current?.(phase === 'ended' ? 'ended' : undefined);
  }, [phase]);

  const setCam = (on) => { call()?.setLocalVideo(on); if (phase !== 'joined') savePrefs({ cam: on }); setTimeout(refresh, 50); };
  const setMic = (on) => { call()?.setLocalAudio(on); if (phase !== 'joined') savePrefs({ mic: on }); setTimeout(refresh, 50); };
  const setDevice = async (kind, id) => {
    const c = call();
    if (!c || !id || id === (kind === 'camera' ? devices.camId : devices.micId)) return;
    try {
      await c.setInputDevicesAsync(kind === 'camera' ? { videoDeviceId: id } : { audioDeviceId: id });
      savePrefs(kind === 'camera' ? { camId: id } : { micId: id });
      setMediaError('');
      await loadDevices();
    } catch { setMediaError("Couldn't switch to that device."); }
  };
  const toggleShare = async () => {
    const c = call();
    if (!c) return;
    try { if (local?.screen) await c.stopScreenShare(); else await c.startScreenShare(); } catch { /* picker cancelled */ }
  };
  const send = useCallback((msg, to = '*') => {
    try { callRef.current?.sendAppMessage(msg, to); return true; } catch { return false; }
  }, []);
  const onApp = useCallback((h) => { handlers.current.add(h); return () => handlers.current.delete(h); }, []);
  const participantsNow = useCallback(() => callRef.current?.participants() || {}, []);

  // Speakers: our own <audio> elements follow devices.speakerId (setSinkId); Daily is told too.
  const setSpeaker = (id) => {
    if (!id) return;
    setDevices((d) => ({ ...d, speakerId: id }));
    savePrefs({ speakerId: id });
    call()?.setOutputDeviceAsync?.({ outputDeviceId: id }).catch(() => {});
  };
  // A one-off picture (bytes, guests) isn't remembered; everything else is, for next time.
  const setEffect = async (eff) => {
    if (!blurSupported) return;
    setEffectBusy(eff.id || eff.type);
    const ok = await applyEffect(eff);
    setEffectBusy(null);
    if (ok && !eff.bytes) savePrefs({ effect: eff.id ? { type: eff.type, id: eff.id } : { type: eff.type }, blur: undefined });
    if (ok && eff.bytes) setLocalBg(eff);
    if (!ok) toast.error(eff.type === 'image' ? "Couldn't use that background picture." : "Couldn't change the background on this device.");
  };
  const setBlur = (on) => setEffect(on ? { type: 'blur' } : NONE);
  // A raised hand lives on the participant (Daily userData), so everyone (late joiners too) sees it.
  const setHand = (on) => {
    const c = call();
    const ud = c?.participants()?.local?.userData || {};
    c?.setUserData({ ...(typeof ud === 'object' ? ud : {}), hand: on ? Date.now() : null }).then(refresh).catch(() => {});
  };
  const lowerHand = (sessionId) => {
    if (sessionId === local?.session_id) setHand(false);
    else send({ mm: 'lower-hand' }, sessionId || '*');
  };

  return { phase, error, mediaError, participants, local, devices, network, micLevel, blur: effect.type === 'blur', effect, effectBusy, localBg, blurSupported,
    activeSpeaker, joinedAt, hostId, setHostId, join, leave, setCam, setMic, setDevice, setSpeaker, setBlur, setEffect, setHand, lowerHand, toggleShare, send, onApp, participantsNow };
}
